import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { requestDetailPath } from "@vv/types";
import { PriorityChip, type ValidationRequest } from "@vv/requests-frontend";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "react-router-dom";

export interface KanbanCardProps {
  request: ValidationRequest;
  teamName: string;
  /** True when the current user can't move this card to any other column - shown as non-draggable. */
  disabled: boolean;
}

export function KanbanCard({ request, teamName, disabled }: KanbanCardProps) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: request.id,
    disabled,
  });

  return (
    <Card
      ref={setNodeRef}
      variant="outlined"
      onClick={() => navigate(requestDetailPath(request.id))}
      sx={{
        cursor: disabled ? "pointer" : "grab",
        opacity: isDragging ? 0.5 : 1,
        transform: CSS.Translate.toString(transform),
        touchAction: "none",
        // Without this, a column with more cards than fit its scroll area
        // gets its cards squashed toward zero height instead of scrolling -
        // flex items default to flex-shrink:1, and overflow:auto on the
        // parent doesn't override that.
        flexShrink: 0,
        "&:hover": { borderColor: "primary.main" },
      }}
      {...(disabled ? {} : { ...listeners, ...attributes })}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {request.displayId} · {teamName}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {request.title}
          </Typography>
          <PriorityChip priority={request.priority} />
        </Stack>
      </CardContent>
    </Card>
  );
}
